import * as Yup from "yup";

// Multi-lingual support
import { t } from "i18next";
import { EValidationErrorType } from "./CommonDynamicFormTypes";

export const activityFormValidationSchema = () => {
  return Yup.object().shape({
    sections: Yup.array().of(
      Yup.object().shape({
        questions: Yup.array().of(
          Yup.object().shape({
            answer: Yup.lazy((answer, context) => {
              const { parent } = context;
              const validationRules = parent?.data?.validation || [];

              let schema = Yup.string();

              validationRules.forEach((rule: string) => {
                if (rule === EValidationErrorType.REQUIRED) {
                  schema = schema.required(
                    t("commonDynamicForm.validations.fieldRequired"),
                  );
                }
                if (rule === EValidationErrorType.ALPHABETS) {
                  schema = schema.matches(
                    /^[A-Za-z\s]+$/,
                    t("commonDynamicForm.validations.onlyAlphabets"),
                  );
                }
                if (rule === EValidationErrorType.NUMERICS) {
                  schema = schema.matches(
                    /^[0-9]+$/,
                    t("commonDynamicForm.validations.onlyNumerics"),
                  );
                }
                if (rule === EValidationErrorType.ALPHANUMERIC) {
                  schema = schema.matches(
                    /^[A-Za-z0-9\s]+$/,
                    t("commonDynamicForm.validations.onlyAlphaNumeric"),
                  );
                }
                if (rule === EValidationErrorType.FUTUREDATE) {
                  schema = schema.test(
                    "future-date",
                    t("commonDynamicForm.validations.futureDateNotAllowed"),
                    (value) => !value || new Date(value) <= new Date(),
                  );
                }
                if (rule === EValidationErrorType.PASTDATE) {
                  schema = schema.test(
                    "past-date",
                    t("commonDynamicForm.validations.pastDatesAreNotAllowed"),
                    (value) => {
                      if (!value) return true;
                      const selectedDate = new Date(value).setHours(0, 0, 0, 0);
                      const today = new Date().setHours(0, 0, 0, 0);
                      return selectedDate >= today;
                    },
                  );
                }
              });

              // apply range bounds validation for range scale controls
              if (parent?.data?.rangeValues) {
                const startRange =
                  parseInt(parent?.data?.rangeValues?.startRange as string) ||
                  0;
                const endRange =
                  parseInt(parent?.data?.rangeValues?.endRange as string) ||
                  100;
                schema = schema.test(
                  "range-bounds",
                  t("commonDynamicForm.validations.enterValueWithinRange"),
                  (value) => {
                    if (!value) return true;
                    const numValue = parseFloat(value);
                    return (
                      !isNaN(numValue) &&
                      numValue >= startRange &&
                      numValue <= endRange
                    );
                  },
                );
              }

              return schema;
            }),

            // ✅ Add validation for nested subForms
            subForms: Yup.array().of(
              Yup.object().shape({
                sections: Yup.array().of(
                  Yup.object().shape({
                    questions: Yup.array().of(
                      Yup.object().shape({
                        answer: Yup.lazy((answer, context) => {
                          const { parent } = context;
                          const validationRules =
                            parent?.data?.validation || [];

                          let schema = Yup.string();

                          validationRules.forEach((rule: string) => {
                            if (rule === EValidationErrorType.REQUIRED) {
                              schema = schema.required(
                                t(
                                  "commonDynamicForm.validations.fieldRequired",
                                ),
                              );
                            }
                            if (rule === EValidationErrorType.ALPHABETS) {
                              schema = schema.matches(
                                /^[A-Za-z\s]+$/,
                                t(
                                  "commonDynamicForm.validations.onlyAlphabets",
                                ),
                              );
                            }
                            if (rule === EValidationErrorType.NUMERICS) {
                              schema = schema.matches(
                                /^[0-9]+$/,
                                t("commonDynamicForm.validations.onlyNumerics"),
                              );
                            }
                            if (rule === EValidationErrorType.ALPHANUMERIC) {
                              schema = schema.matches(
                                /^[A-Za-z0-9\s]+$/,
                                t(
                                  "commonDynamicForm.validations.onlyAlphaNumeric",
                                ),
                              );
                            }
                            if (rule === EValidationErrorType.FUTUREDATE) {
                              schema = schema.test(
                                "future-date",
                                t(
                                  "commonDynamicForm.validations.futureDateNotAllowed",
                                ),
                                (value) =>
                                  !value || new Date(value) <= new Date(),
                              );
                            }
                            if (rule === EValidationErrorType.PASTDATE) {
                              schema = schema.test(
                                "past-date",
                                t(
                                  "commonDynamicForm.validations.pastDatesAreNotAllowed",
                                ),
                                (value) => {
                                  if (!value) return true;
                                  const selectedDate = new Date(value).setHours(
                                    0,
                                    0,
                                    0,
                                    0,
                                  );
                                  const today = new Date().setHours(0, 0, 0, 0);
                                  return selectedDate >= today;
                                },
                              );
                            }
                          });

                          //  apply range bounds validation for range scale controls in subforms
                          if (parent?.data?.rangeValues) {
                            const startRange =
                              parseInt(
                                parent?.data?.rangeValues?.startRange as string,
                              ) || 0;
                            const endRange =
                              parseInt(
                                parent?.data?.rangeValues?.endRange as string,
                              ) || 100;
                            schema = schema.test(
                              "range-bounds",
                              t(
                                "commonDynamicForm.validations.enterValueWithinRange",
                              ),
                              (value) => {
                                if (!value) return true;
                                const numValue = parseFloat(value);
                                return (
                                  !isNaN(numValue) &&
                                  numValue >= startRange &&
                                  numValue <= endRange
                                );
                              },
                            );
                          }
                          return schema;
                        }),
                      }),
                    ),
                  }),
                ),
              }),
            ),
          }),
        ),
      }),
    ),
  });
};
